import React from 'react'

import {FormikProps} from 'formik'
import get from 'lodash/get'
import * as Yup from 'yup'

import config from '../../../config'

import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import {formatCurrency, formatPrice} from '../../../libs/formatter'
import {calculatePricesForShipping} from '../../../libs/pricing'
import {PossiblyMixedValue, isMixedValue, unlockMixedValuesLabel} from '../../utils/form'

import {CountryCode, Provider, ShippingType} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import InputField from '../../../../../commons/src/components/input-field'
import NumberInputField from '../../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../../commons/src/components/paragraph'
import RadioGroupField from '../../../../../commons/src/components/radio-group-field/radio-group-field'
import RadioGroupItem from '../../../../../commons/src/components/radio-group-field/radio-group-item'
import Repeatable from '../../../../../commons/src/components/repeatable'
import Stack from '../../../../../commons/src/components/stack'
import MixedValueFormSection from '../../../components/mixed-value-form-section'
import MixedValueInputWrapper from '../../../components/mixed-value-input-wrapper'

export enum ShippingPriceType {
  SINGLE = 'single',
  WEIGHT = 'weight',
}

export type StaggeredWeightPrice = {weight: string; price: string}
export type ShippingPrice = string | StaggeredWeightPrice[]

export type SingleFormData = {
  name: string
  price: ShippingPrice
  priceType: ShippingPriceType
  deliveryTimeFast: string
  deliveryTimeSlow: string
  type: ShippingType
  countryCodes: CountryCode[]
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export type MixedFormData = {
  name: PossiblyMixedValue<string>
  price: PossiblyMixedValue<ShippingPrice>
  priceType: PossiblyMixedValue<ShippingPriceType>
  deliveryTimeFast: PossiblyMixedValue<string>
  deliveryTimeSlow: PossiblyMixedValue<string>
  type: PossiblyMixedValue<ShippingType>
  countryCodes: PossiblyMixedValue<CountryCode[]>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export const initialValues: SingleFormData = {
  name: '',
  price: '',
  priceType: ShippingPriceType.SINGLE,
  deliveryTimeFast: '',
  deliveryTimeSlow: '',
  type: 'standard',
  countryCodes: [],
}

export const validationSchema = {
  name: Yup.string().label('Name').required(),
  // options.originalValue is not typed in Yup ResolveOptions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  price: Yup.lazy((value: ShippingPrice, options: any) => {
    // As we are nesting `Lazy` instances when using `toMixedValidationSchema`, `value` can be undefined
    // at validation time, so we need to rely on the originalValue for determining the ShippingPriceType.
    const lazyValue = value || options.originalValue

    if (Array.isArray(lazyValue)) {
      // ShippingPriceType.WEIGHT
      return Yup.array()
        .label('Staggered weight price')
        .of(
          Yup.object()
            .shape({
              weight: Yup.number().label('Weight threshold').min(0).required(),
              price: Yup.number().label('Price').min(0).required(),
            })
            .test('unique', 'Weight threshold must be unique', (val, ctx) => {
              if (
                val &&
                ctx.parent.filter((price: typeof val) => price.weight === val.weight).length > 1
              ) {
                return ctx.createError({path: `${ctx.path}.weight`})
              }
              return true
            })
        )
    }

    // ShippingPriceType.SINGLE
    return Yup.number()
      .label('Fixed price')
      .min(0)
      .when(['type'], {
        is: (type: string) => type === 'standard',
        then: schema => schema.max(100),
      })
      .when(['type'], {
        is: (type: string) => type === 'express',
        then: schema => schema.max(150),
      })
      .required()
  }),
  deliveryTimeFast: Yup.number().label('Min delivery time').integer().min(1).max(99).required(),
  deliveryTimeSlow: Yup.number()
    .label('Max delivery time')
    .integer()
    .min(1)
    .when(['type'], {
      is: (type: string) => type === 'standard',
      then: schema => schema.min(2),
    })
    .max(99)
    .required()
    .test(
      'interval',
      'Max delivery time has to be equal or larger than min delivery time',
      (value, ctx) => {
        if (value && ctx.parent.deliveryTimeFast && ctx.parent.deliveryTimeFast > value) {
          return ctx.createError({path: ctx.path})
        }
        return true
      }
    ),
  countryCodes: Yup.array().min(1, 'At least one country has to be selected').required(),
}

type Props = {
  formik: FormikProps<MixedFormData> | FormikProps<SingleFormData>
  provider: Provider
  children?: React.ReactNode
}

const ShippingsForm = ({formik, provider, children}: Props) => {
  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  const getInitialStaggeredWeightPrice = (index: number) => ({
    weight: index === 0 ? '0' : '',
    price: '',
  })

  const setPriceType = (priceType: ShippingPriceType) => {
    let price: SingleFormData['price'] = initialValues.price

    if (priceType === ShippingPriceType.WEIGHT) {
      price = [getInitialStaggeredWeightPrice(0), getInitialStaggeredWeightPrice(1)]
    }

    formik.setFieldValue('priceType', priceType)
    formik.setFieldValue('price', price)
  }

  const setPossiblyMixedPriceType = () => {
    let nextPriceType = initialValues.priceType

    if (!isMixedValue(formik.values.priceType)) {
      nextPriceType = formik.values.priceType
    }

    setPriceType(nextPriceType)
  }

  const renderSingleShippingPrice = () => {
    const isMixed = isMixedValue(formik.values.price)
    const pricings = calculatePricesForShipping(
      formik.values.price ? parseFloat(formik.values.price as string) : 0,
      config.priceCalculation.craftcloudMargin,
      config.priceCalculation.profitShare
    )

    return (
      <FormRow layout={isMixed ? undefined : 'half-half'}>
        <FieldNote
          type="error"
          note={
            formik.submitCount > 0 && typeof formik.errors.price === 'string' && formik.errors.price
          }
        >
          <MixedValueInputWrapper
            locked={isMixed}
            onClick={() => formik.setFieldValue('price', initialValues.price)}
            unlockButtonIconTitle={unlockMixedValuesLabel}
          >
            <NumberInputField
              allowDecimals
              label="Price"
              name="price"
              mixedLabel="Mixed"
              mixed={isMixed}
              value={isMixed ? (initialValues.price as string) : (formik.values.price as string)}
              suffix={formatCurrency(provider.currency)}
              error={formik.submitCount > 0 && !!formik.errors.price}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </MixedValueInputWrapper>
        </FieldNote>
        {!isMixed && (
          <Stack space="60">
            <Paragraph minor size="s" classNames={['u-no-margin-bottom']}>
              Final Customer price:{' '}
              {formik.values.price
                ? formatPrice(pricings.totalCustomerPriceV2, provider.currency)
                : 'n/a'}
            </Paragraph>
            <Paragraph minor size="s" classNames={['u-no-margin-bottom']}>
              Your earnings:{' '}
              {formik.values.price
                ? formatPrice(pricings.invoiceableAmountV2, provider.currency)
                : 'n/a'}
            </Paragraph>
          </Stack>
        )}
      </FormRow>
    )
  }

  const renderWeightShippingPrice = () => (
    <FormRow>
      <Repeatable
        preventDeleteMin
        min={2}
        button={<Button hierarchy="secondary">Add threshold</Button>}
        onAdd={nextIndex => {
          formik.setFieldValue('price', [
            ...(formik.values.price as StaggeredWeightPrice[]),
            getInitialStaggeredWeightPrice(nextIndex),
          ])
        }}
        onDelete={index => {
          formik.setFieldValue(
            'price',
            (formik.values.price as StaggeredWeightPrice[]).filter((_, i) => i !== index)
          )
        }}
      >
        {(formik.values.price as StaggeredWeightPrice[]).map((price, index) => {
          const pricings = calculatePricesForShipping(
            price.price ? parseFloat(price.price) : 0,
            config.priceCalculation.craftcloudMargin,
            config.priceCalculation.profitShare
          )

          return (
            <FormRow key={index} layout="thirds">
              <FieldNote
                type="error"
                note={formik.submitCount > 0 && get(formik.errors, `price[${index}].weight`)}
              >
                <NumberInputField
                  allowDecimals
                  label="Weight threshold"
                  name={`price[${index}].weight`}
                  value={price.weight}
                  disabled={index === 0}
                  prefix="≥"
                  suffix="kg"
                  error={formik.submitCount > 0 && get(formik.errors, `price[${index}].weight`)}
                  onChange={(value, name) => formik.setFieldValue(name as string, value)}
                  onBlur={formik.handleBlur}
                />
              </FieldNote>
              <FieldNote
                type="error"
                note={formik.submitCount > 0 && get(formik.errors, `price[${index}].price`)}
              >
                <NumberInputField
                  allowDecimals
                  label="Price"
                  name={`price[${index}].price`}
                  value={price.price}
                  suffix={formatCurrency(provider.currency)}
                  error={formik.submitCount > 0 && get(formik.errors, `price[${index}].price`)}
                  onChange={(value, name) => formik.setFieldValue(name as string, value)}
                  onBlur={formik.handleBlur}
                />
              </FieldNote>
              <Stack space="60">
                <Paragraph minor size="s" classNames={['u-no-margin-bottom']}>
                  Final Customer price:{' '}
                  {price.price
                    ? formatPrice(pricings.totalCustomerPriceV2, provider.currency)
                    : 'n/a'}
                </Paragraph>
                <Paragraph minor size="s" classNames={['u-no-margin-bottom']}>
                  Your earnings:{' '}
                  {price.price
                    ? formatPrice(pricings.invoiceableAmountV2, provider.currency)
                    : 'n/a'}
                </Paragraph>
              </Stack>
            </FormRow>
          )
        })}
      </Repeatable>
    </FormRow>
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <FormRow>
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.name}>
          <MixedValueInputWrapper
            locked={isMixedValue(formik.values.name)}
            onClick={() => formik.setFieldValue('name', initialValues.name)}
            unlockButtonIconTitle={unlockMixedValuesLabel}
          >
            <InputField
              label="Name (for your internal reference only)"
              name="name"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.name)}
              value={isMixedValue(formik.values.name) ? initialValues.name : formik.values.name}
              error={formik.submitCount > 0 && !!formik.errors.name}
              onChange={(value, name, event) => formik.handleChange(event)}
              onBlur={formik.handleBlur}
            />
          </MixedValueInputWrapper>
        </FieldNote>
      </FormRow>
      <Headline size="s" label="Pricing type" classNames={['u-margin-top-l']} />
      <MixedValueFormSection
        locked={isMixedValue(formik.values.priceType) || isMixedValue(formik.values.price)}
        onClick={setPossiblyMixedPriceType}
        unlockButtonLabel={unlockMixedValuesLabel}
      >
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.priceType}>
          <RadioGroupField
            horizontal
            name="priceType"
            value={
              isMixedValue(formik.values.priceType)
                ? initialValues.priceType
                : formik.values.priceType
            }
            onChange={value => setPriceType(value as ShippingPriceType)}
          >
            <RadioGroupItem label="Fixed price" value={ShippingPriceType.SINGLE} />
            <RadioGroupItem label="Weight price" value={ShippingPriceType.WEIGHT} />
          </RadioGroupField>
        </FieldNote>
        {(formik.values.priceType === ShippingPriceType.SINGLE ||
          isMixedValue(formik.values.priceType) ||
          isMixedValue(formik.values.price)) &&
          renderSingleShippingPrice()}
        {formik.values.priceType === ShippingPriceType.WEIGHT &&
          !isMixedValue(formik.values.price) &&
          renderWeightShippingPrice()}
      </MixedValueFormSection>
      <Headline size="s" label="Shipping type" classNames={['u-margin-top-l']} />
      <MixedValueFormSection
        locked={isMixedValue(formik.values.type)}
        onClick={() => formik.setFieldValue('type', initialValues.type)}
        unlockButtonLabel={unlockMixedValuesLabel}
      >
        <FormRow>
          <RadioGroupField
            name="type"
            horizontal
            value={isMixedValue(formik.values.type) ? initialValues.type : formik.values.type}
            onChange={(value, name) => {
              return formik.setFieldValue(name || '', value, false)
            }}
          >
            <RadioGroupItem value="standard" label="Standard" />
            <RadioGroupItem value="express" label="Express" />
          </RadioGroupField>
        </FormRow>
      </MixedValueFormSection>
      <Headline size="s" label="Delivery time" classNames={['u-margin-top-l']} />
      <MixedValueFormSection
        locked={
          isMixedValue(formik.values.deliveryTimeFast) ||
          isMixedValue(formik.values.deliveryTimeSlow)
        }
        onClick={() => {
          formik.setFieldValue(
            'deliveryTimeFast',
            isMixedValue(formik.values.deliveryTimeFast)
              ? initialValues.deliveryTimeFast
              : formik.values.deliveryTimeFast
          )
          formik.setFieldValue(
            'deliveryTimeSlow',
            isMixedValue(formik.values.deliveryTimeSlow)
              ? initialValues.deliveryTimeSlow
              : formik.values.deliveryTimeSlow
          )
        }}
        unlockButtonLabel={unlockMixedValuesLabel}
      >
        <FormRow layout="half-half" divider="-">
          <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.deliveryTimeFast}>
            <NumberInputField
              label="min"
              name="deliveryTimeFast"
              suffix="business day(s)"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.deliveryTimeFast)}
              value={
                isMixedValue(formik.values.deliveryTimeFast)
                  ? initialValues.deliveryTimeFast
                  : formik.values.deliveryTimeFast
              }
              error={formik.submitCount > 0 && !!formik.errors.deliveryTimeFast}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
          <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.deliveryTimeSlow}>
            <NumberInputField
              label="max"
              name="deliveryTimeSlow"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.deliveryTimeSlow)}
              value={
                isMixedValue(formik.values.deliveryTimeSlow)
                  ? initialValues.deliveryTimeSlow
                  : formik.values.deliveryTimeSlow
              }
              suffix="business day(s)"
              error={formik.submitCount > 0 && !!formik.errors.deliveryTimeSlow}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
      </MixedValueFormSection>
      {children}
    </form>
  )
}

export default ShippingsForm
